import React, { useEffect, useRef, FC } from 'react';
import type { Location } from 'react-router-dom';

import type { PostHogConfig, PostHog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

export interface AnalyticsProps {
  children: React.ReactNode;
  apiKey: string | undefined;
  apiHost: string | undefined;
  uiHost: string | undefined;
  options?: Partial<PostHogConfig>;
  enabled: boolean;
}

export const AnalyticsProvider: React.FC<AnalyticsProps> = ({
  enabled,
  apiKey,
  apiHost,
  uiHost,
  children,
  options = {},
}) => {
  if (!enabled) {
    return <>{children}</>;
  }

  if (!apiKey || !apiHost) {
    console.warn(
      '[Analytics] enabled=true but apiKey/apiHost missing — analytics disabled. Check REACT_APP_POST_HOG_PROJECT_API_KEY / REACT_APP_POSTHOG_API_HOST.',
    );

    return <>{children}</>;
  }

  const composedOptions = {
    api_host: apiHost,
    ui_host: uiHost,
    ...options,
  };

  return (
    <PostHogProvider apiKey={apiKey} options={composedOptions}>
      {children}
    </PostHogProvider>
  );
};

interface PostHogPageViewProps {
  children: React.ReactNode;
  posthog: PostHog | null;
  location: Location;
}

const generatePageviewId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for non-secure contexts / very old browsers. Collision risk is
  // session-scoped; uniqueness within a session is all we need for
  // $pageview_id ↔ replay linkage.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Route-alias redirects (e.g. /solution-session/X -> /office-hours/X) cause
// React Router to emit two location changes within ~50-60ms. Without
// debouncing, both fire $pageview events and the second carries a self-prev
// artifact because PostHog's internal pageview tracker is updated by the
// first capture and races our explicit prev. Suppress the first (the
// transient origin URL the user never actually sees rendered) by debouncing
// the fire — if the pathname changes again before the timer elapses, we
// reschedule with the new path.
const REDIRECT_DEBOUNCE_MS = 80;

/**
 * Fires `$pageview` on every `location.pathname` change. Synthesizes the
 * `$pageview_id` + `$prev_pageview_*` properties that PostHog auto-capture
 * normally attaches, so replay→event linkage and Paths-insight breadcrumbs
 * keep working. We do this manually because React Router 6 navigations
 * don't reliably trigger PostHog's `pushState` hook on this codebase —
 * auto-capture pageviews silently miss most SPA navs.
 *
 * `posthog.register({ $pageview_id })` writes the id as a super-property so
 * subsequent events (autocapture clicks, rageclicks, etc.) ride with the
 * same id between pageviews — matching PostHog's native behavior.
 *
 * Caller must set `capture_pageview: false` in PostHog init options to
 * avoid duplicate auto-pageviews.
 */
export const AnalyticsPageView: FC<PostHogPageViewProps> = ({ children, posthog, location }) => {
  const lastPathRef = useRef<string | null>(null);
  const lastUrlRef = useRef<string | null>(null);
  const viewStartRef = useRef<number>(Date.now());
  const fireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!posthog) return;
    if (lastPathRef.current === location.pathname) return;

    // Cancel any pending fire from a prior pathname change. If we're inside
    // a redirect chain, this drops the transient intermediate URL.
    if (fireTimerRef.current) clearTimeout(fireTimerRef.current);

    fireTimerRef.current = setTimeout(() => {
      fireTimerRef.current = null;

      const now = Date.now();
      const currentUrl = window.location.href;
      const pageviewId = generatePageviewId();

      posthog.register({ $pageview_id: pageviewId });

      const properties: Record<string, unknown> = {
        $current_url: currentUrl,
        $pathname: location.pathname,
        $pageview_id: pageviewId,
      };

      if (lastPathRef.current !== null) {
        properties.$prev_pageview_pathname = lastPathRef.current;
        properties.$prev_pageview_url = lastUrlRef.current;
        properties.$prev_pageview_duration = Math.round((now - viewStartRef.current) / 100) / 10;
      }

      posthog.capture('$pageview', properties);

      lastPathRef.current = location.pathname;
      lastUrlRef.current = currentUrl;
      viewStartRef.current = now;
    }, REDIRECT_DEBOUNCE_MS);

    return () => {
      if (fireTimerRef.current) {
        clearTimeout(fireTimerRef.current);
        fireTimerRef.current = null;
      }
    };
  }, [location, posthog]);

  return <>{children}</>;
};
